import { Link } from "react-router";
import classNames from "classnames";
import { ColumnDef } from "@tanstack/react-table";

import { SourceUsageTableRow, SourceTableStyles } from "./types";
import { getUsageColorByDomainType } from "./utils/getUsageColorByDomainType";
import { ArrawRightIcon } from "../icons";

export interface SourceTableColumnsOptions {
  showDomainType?: boolean;
}

export function getSourceTableColumns(
  styles: SourceTableStyles,
  options: SourceTableColumnsOptions = {}
): ColumnDef<SourceUsageTableRow>[] {
  const { showDomainType = true } = options;

  const columns: ColumnDef<SourceUsageTableRow>[] = [
    {
      id: "index",
      accessorKey: "index",
      header: "#",
      cell: ({ getValue }) => getValue() as number,
    },
    {
      id: "source",
      accessorKey: "name",
      header: "Source",
      cell: ({ row }) => {
        const rowData = row.original;
        return (
          <div className={styles["source_table__source"]}>
            <div className={styles["source_table__logo"]}>
              {rowData.logoUrl ? (
                <img
                  src={rowData.logoUrl}
                  alt=""
                />
              ) : (
                rowData.logoText ?? rowData.name.slice(0, 2).toUpperCase()
              )}
            </div>
            <h3 className={styles["source_table__name"]}>
              {rowData.name}
            </h3>
          </div>
        );
      },
    },
    ...(showDomainType
      ? [
          {
            id: "domainType",
            accessorKey: "domainType",
            header: "Domain type",
            cell: ({ row }: { row: { original: SourceUsageTableRow } }) => {
              const value = row.original.domainType;
              const domainClass = getUsageColorByDomainType(value);

              return (
                <span
                  className={classNames(
                    styles["source_table__usage"],
                    styles[domainClass]
                  )}
                >
                  {value.toUpperCase()}
                </span>
              );
            },
          } as ColumnDef<SourceUsageTableRow>,
        ]
      : []),
    {
      id: "linksCount",
      accessorKey: "linksCount",
      header: "LINKS",
      cell: ({ getValue }) => getValue() as number,
    },
    {
      id: "usagePercent",
      accessorKey: "usagePercent",
      header: "Usage",
      cell: ({ getValue }) => `${Math.round(getValue() as number)}%`,
    },
    {
      id: "citations",
      accessorKey: "averageCitations",
      header: "Citations",
      cell: ({ getValue }) => {
        const value = getValue() as number | null;
        if (value == null) return "—";
        return Number.isInteger(value) ? value : value.toFixed(2);
      },
    },
    {
      id: "link",
      accessorKey: "detailHref",
      header: () => null,
      cell: ({ row }) => (
        <Link
          to={row.original.detailHref}
          className={styles["source_table__link"]}
          aria-label="Open source details"
          onClick={(e) => e.stopPropagation()}
        >
          <ArrawRightIcon
            className={styles["source_table__link_icon"]}
          />
        </Link>
      ),
      meta: { ariaHidden: true },
    },
  ];

  return columns;
}
